import Layout from "modules/layout/dashboard"

import React, { useEffect, useRef } from "react"
import styles from "./styles.module.scss"
import { useNavigate, useSearchParams } from "react-router-dom"
import Loader from "components/loader"
import EventOverview from "components/overview-event"
import { useEvents } from "pages/events/queries"
import { Button, Flex, Input, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { usePopup } from "utils/use-popup"
import TerminateEventModal from "../events/terminateEventModal"
import StartEventModal from "../events/startEventModal"
import { useIntersectionObserver } from "utils/use-intersection-observer"
import { useDebounce } from "utils/use-Debounce"
import { ReactComponent as FilterIcon } from "assets/icons/filter.svg"
import FilterModal from "./filter-modal"
import { RangeValue, useFiltersForm } from "./use-form"
import dayjs, { Dayjs } from "dayjs"

const { Main, Header } = Layout

const ACTIVITY_PARAM = "activity"

const EventMonitoring: React.FC = () => {
  const { t } = useTranslation()
  const startEventModal = usePopup<string>()
  const filterModal = usePopup()
  const terminateEventModal = usePopup<string>()
  const navigate = useNavigate()
  let [searchParams, setSearchParams] = useSearchParams()
  const debouncesSearchParams = useDebounce<URLSearchParams>(searchParams)
  const [observerRef, entry] = useIntersectionObserver({
    threshold: 0,
  })
  // to stop it from fetching 2 pages at a time
  const fetchedPages = useRef(new Set<any>())

  const form = useFiltersForm({
    status: searchParams.get("status") ?? undefined,
    start_date: searchParams.get("start_date[values][0]")
      ? {
          operator: searchParams.get("start_date[operator]")!,
          values: [
            dayjs(searchParams.get("start_date[values][0]")),
            searchParams.get("start_date[values][1]")
              ? dayjs(searchParams.get("start_date[values][1]"))
              : null,
          ].filter((date) => !!date) as RangeValue<Dayjs | null>,
        }
      : { operator: "equals" },
  })

  const onSubmit = form.handleSubmit((data) => {
    if (data.status) searchParams.set("status", data.status)
    else searchParams.delete("status")

    const startDateValues = data.start_date?.values?.filter((date) => !!date)

    /** other date  operators expect between */
    if (
      data.start_date?.operator !== "between" &&
      startDateValues &&
      startDateValues.length === 1
    ) {
      searchParams.set("start_date[operator]", data.start_date?.operator!)
      searchParams.set(
        "start_date[values][0]",
        dayjs(startDateValues[0]).format("YYYY-MM-DD")
      )
      searchParams.delete("start_date[values][1]")
    } else if (startDateValues && startDateValues.length > 1) {
      /** date between operator */
      searchParams.set("start_date[operator]", data.start_date?.operator!)
      searchParams.set(
        "start_date[values][0]",
        dayjs(startDateValues[0]).format("YYYY-MM-DD")
      )
      searchParams.set(
        "start_date[values][1]",
        dayjs(startDateValues[1]).format("YYYY-MM-DD")
      )
    } else {
      searchParams.delete("start_date[operator]")
      searchParams.delete("start_date[values][0]")
      searchParams.delete("start_date[values][1]")
    }
    filterModal.onClose()
    setSearchParams(searchParams, { replace: true })
    events.remove()
  })

  const events = useEvents({
    eventName: debouncesSearchParams.get(ACTIVITY_PARAM),
    start_date: searchParams.get("start_date[values][0]")
      ? {
          operator: searchParams.get("start_date[operator]")!,
          values: [
            searchParams.get("start_date[values][0]"),
            searchParams.get("start_date[values][1]"),
          ].filter((date) => !!date),
        }
      : null,
    status: searchParams.get("status"),
  })

  useEffect(() => {
    if (
      entry?.isIntersecting &&
      events.hasNextPage &&
      !events.isFetchingNextPage
    ) {
      if (!fetchedPages.current.has(entry.target)) {
        fetchedPages.current.add(entry.target)
        events.fetchNextPage()
      }
    }
  }, [
    entry?.isIntersecting,
    events.hasNextPage,
    events.isFetchingNextPage,
    events.data?.pages.length,
  ])

  return (
    <Layout>
      <Header />
      <div className={styles.filterContainer}>
        <div className={styles.filterWrapper}>
          <Input
            className={styles.searchInput}
            size="large"
            placeholder={t("global.search")}
            value={searchParams.get(ACTIVITY_PARAM) ?? ""}
            onChange={(e) => {
              e.target.value
                ? searchParams.set(ACTIVITY_PARAM, e.target.value)
                : searchParams.delete(ACTIVITY_PARAM)
              setSearchParams(searchParams, { replace: true })
            }}
          />
          <Button
            type="text"
            size="large"
            shape="circle"
            onClick={filterModal.open}
            icon={<FilterIcon height={24} />}
          />
        </div>
      </div>
      <Main overflow>
        {events.isLoading || events.isRefetching ? (
          <Loader />
        ) : (
          <div className={styles.container}>
            <div className={styles.members}>
              {events.data?.pages
                .flatMap((page) => page.data)
                .map((item, index, array) => (
                  <EventOverview
                    key={item.id}
                    ref={index === array.length - 1 ? observerRef : undefined}
                    item={item}
                    onButtonClick={() => navigate(`${item.id}`)}
                  />
                ))}
              {events.isFetchingNextPage && (
                <Flex
                  style={{
                    width: "100%",
                    marginTop: "var(--size-8)",
                    marginBottom: "var(--size-8)",
                  }}
                  justify="center"
                >
                  <Spin />
                </Flex>
              )}
            </div>
          </div>
        )}
        <TerminateEventModal
          {...terminateEventModal}
          open={terminateEventModal.isOpen}
        />
        <StartEventModal {...startEventModal} open={startEventModal.isOpen} />
        <FilterModal
          {...filterModal}
          open={filterModal.isOpen}
          form={form}
          onSubmit={onSubmit}
        />
      </Main>
    </Layout>
  )
}

export default EventMonitoring
